import React, { useEffect, useState } from "react";
import { Button, Input, Modal, notification, Spin, Tag } from "antd";
import { ArrowLeftOutlined, CheckCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";

import SmartRtpLayout from "./components/SmartRtpLayout";
import { usePermissions } from "../../../controllers/PermissionContext";
import { formatSmartRtpNumber } from "../../../utils/bigMath";
import {
  cancelOrder,
  getOrderDetails,
} from "../../../controllers/V1/smartRtp/smartRtpController";

export default function SmartRtpOrderDetailsPage() {
  const PERMISSION = usePermissions("smartRtp");
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("editId") || location.state?.editId || null;
  const orderNo = searchParams.get("order_no") || location.state?.order_no || null;

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const loadDetails = async () => {
    if (!editId && !orderNo) {
      setLoading(false);
      setOrder(null);
      return;
    }
    setLoading(true);
    const res = await getOrderDetails({
      ...(editId ? { editId } : {}),
      ...(orderNo ? { order_no: orderNo } : {}),
    });
    if (res.status === true) {
      setOrder(res.result);
    } else {
      setOrder(null);
      notification.error({
        message: "Error",
        description: res.message || "Order not found.",
        icon: <InfoCircleOutlined style={{ color: "red" }} />,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    document.title = "Crown King || Smart RTP Order Details";
    if (
      PERMISSION &&
      !(
        PERMISSION.fullAccess === "Y" ||
        PERMISSION.view === "Y" ||
        PERMISSION.list === "Y"
      )
    ) {
      navigate("/dashboard");
      return;
    }
    loadDetails();
  }, [editId, orderNo]);

  const handleCancel = async () => {
    if (!order?._id) return;
    setCancelling(true);
    const res = await cancelOrder({ editId: order._id, remarks });
    setCancelling(false);
    if (res.status === true) {
      notification.success({
        message: "Success",
        description: "Order cancelled.",
        icon: <CheckCircleOutlined style={{ color: "green" }} />,
      });
      setCancelOpen(false);
      setOrder(res.result);
      loadDetails();
    } else {
      notification.error({
        message: "Error",
        description: res.message || "Unable to cancel order.",
        icon: <InfoCircleOutlined style={{ color: "red" }} />,
      });
    }
  };

  const tickets = order?.tickets || [];
  const seller = order?.seller || {};
  const rtp = order?.rtp_snapshot || {};
  const prize = order?.prize_allocation || {};

  return (
    <SmartRtpLayout
      title="Smart RTP Order Details"
      activeKey="orders"
      headerVariant="simple"
      actions={
        <Link to="/smart-rtp/orders">
          <Button type="secondary">
            <ArrowLeftOutlined /> Back
          </Button>
        </Link>
      }
    >
      {loading ? (
        <div className="card shadow mb-4 p-4 text-center">
          <Spin />
        </div>
      ) : !order ? (
        <div className="card shadow mb-4 p-4">Order not found.</div>
      ) : (
        <>
          <div className="card shadow mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-1">Order {order.order_no}</h5>
                  <Tag color={order.status === "Success" ? "green" : order.status === "Cancel" ? "orange" : "red"}>
                    {order.status}
                  </Tag>
                  {prize.allocated ? <Tag color="blue">Win</Tag> : <Tag>No Win</Tag>}
                </div>
                {order.status === "Success" ? (
                  <Button danger onClick={() => setCancelOpen(true)}>
                    Cancel Order
                  </Button>
                ) : null}
              </div>

              <div className="row">
                <div className="col-md-4 mb-2"><strong>Game:</strong> {order.game_title} ({order.game_code})</div>
                <div className="col-md-4 mb-2"><strong>Tickets:</strong> {order.total_qty}</div>
                <div className="col-md-4 mb-2"><strong>Points:</strong> {formatSmartRtpNumber(order.total_points)}</div>
                <div className="col-md-4 mb-2"><strong>Ticket Price:</strong> {formatSmartRtpNumber(order.ticket_price)}</div>
                <div className="col-md-4 mb-2"><strong>Commission:</strong> {formatSmartRtpNumber(order.commission_points)} ({formatSmartRtpNumber(order.commission_percentage)}%)</div>
                <div className="col-md-4 mb-2"><strong>Reward:</strong> {formatSmartRtpNumber(prize.total_reward || 0)}</div>
                <div className="col-md-4 mb-2"><strong>Buyer Ref:</strong> {order.buyer_ref || "-"}</div>
                <div className="col-md-4 mb-2">
                  <strong>Seller:</strong>{" "}
                  {`${seller.first_name || ""} ${seller.last_name || ""}`.trim() || "-"}
                  {seller.pos_number ? ` · POS ${seller.pos_number}` : ""}
                </div>
                <div className="col-md-4 mb-2"><strong>POS Serial:</strong> {seller.pos_serial_id || "-"}</div>
                <div className="col-md-4 mb-2">
                  <strong>Created:</strong>{" "}
                  {order.createdAt ? moment(order.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-"}
                </div>
                <div className="col-md-4 mb-2"><strong>Opening Bal:</strong> {formatSmartRtpNumber(order.opening_balance)}</div>
                <div className="col-md-4 mb-2"><strong>Closing Bal:</strong> {formatSmartRtpNumber(order.closing_balance)}</div>
              </div>

              <hr />
              <h6>RTP Snapshot</h6>
              <div className="row">
                <div className="col-md-3 mb-2"><strong>Buyer Score:</strong> {formatSmartRtpNumber(rtp.buyer_score)}</div>
                <div className="col-md-3 mb-2"><strong>P Final:</strong> {formatSmartRtpNumber(rtp.p_final)}</div>
                <div className="col-md-3 mb-2"><strong>Budget Factor:</strong> {formatSmartRtpNumber(rtp.budget_factor)}</div>
                <div className="col-md-3 mb-2"><strong>Pool Balance:</strong> {formatSmartRtpNumber(rtp.pool_balance)}</div>
                <div className="col-md-3 mb-2"><strong>Target RTP:</strong> {formatSmartRtpNumber(rtp.game_rtp_target ?? rtp.global_rtp_target)}</div>
                <div className="col-md-3 mb-2"><strong>Policy:</strong> {rtp.engine_policy_version || "-"}</div>
              </div>
            </div>
          </div>

          <div className="card shadow mb-4">
            <div className="card-body">
              <h6 className="mb-3">Tickets</h6>
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Ticket</th>
                      <th>Points</th>
                      <th>Winner</th>
                      <th>Matched</th>
                      <th>Reward</th>
                      <th>Category</th>
                      <th>Draw Number</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.length ? (
                      tickets.map((ticket) => (
                        <tr key={ticket._id || ticket.ticket_index}>
                          <td>{ticket.ticket_index}</td>
                          <td>{(ticket.numbers || []).join(", ")}</td>
                          <td>{formatSmartRtpNumber(ticket.points)}</td>
                          <td>{ticket.match_result?.is_winner ? "Yes" : "No"}</td>
                          <td>
                            {(ticket.match_result?.matched_numbers || []).join(", ") ||
                              ticket.match_result?.matched_count ||
                              0}
                          </td>
                          <td>
                            {(() => {
                              const amount = Number(ticket.match_result?.reward_amount) || 0;
                              const product = ticket.match_result?.product_name;
                              if (product && amount) {
                                return `${formatSmartRtpNumber(amount)} + ${product}`;
                              }
                              if (product) return product;
                              return formatSmartRtpNumber(amount);
                            })()}
                          </td>
                          <td>{ticket.match_result?.prize_category || "-"}</td>
                          <td>
                            {(ticket.match_result?.winning_numbers || []).join(", ") || "-"}
                          </td>
                          <td>{ticket.status}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} className="text-center">
                          No tickets
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {tickets.some((ticket) => (ticket.match_result?.prize_board || []).length) ? (
            <div className="card shadow mb-4">
              <div className="card-body">
                <h6 className="mb-3">Prize Board (20 slots · up to 2 matches)</h6>
                {tickets.map((ticket) => {
                  const board = ticket.match_result?.prize_board || [];
                  if (!board.length) return null;
                  return (
                    <div key={`board-${ticket._id || ticket.ticket_index}`} className="mb-3">
                      <div className="mb-2">
                        Ticket #{ticket.ticket_index} · matched{" "}
                        {(ticket.match_result?.matched_numbers || []).join(", ") || "-"}
                      </div>
                      <div className="row">
                        {board.map((slot) => (
                          <div className="col-6 col-md-3 col-lg-2 mb-2" key={`${slot.slot_index}-${slot.number}`}>
                            <div
                              className="border rounded p-2 h-100"
                              style={{
                                background: slot.is_match ? "#e6f7e6" : "#fff",
                                borderColor: slot.is_match ? "#52c41a" : undefined,
                              }}
                            >
                              <div><strong>#{slot.slot_index}</strong> · {slot.number}</div>
                              <div>
                                {slot.prize_category || (slot.is_display_only ? "Display" : "-")} ·{" "}
                                {slot.product_name
                                  ? slot.prize_kind === "Product" || !slot.prize_amount
                                    ? slot.product_name
                                    : `${slot.product_name} · ${slot.prize_amount}`
                                  : slot.prize_amount || 0}
                              </div>
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                {slot.is_match
                                  ? `${slot.slab_code || slot.slab_name || ""} · MATCH`
                                  : "unmatched (display only)"}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </>
      )}

      <Modal
        title="Cancel Order"
        open={cancelOpen}
        onCancel={() => setCancelOpen(false)}
        onOk={handleCancel}
        confirmLoading={cancelling}
        okText="Cancel Order"
        okButtonProps={{ danger: true }}
      >
        <p>Cancel order <strong>{order?.order_no}</strong>?</p>
        <Input.TextArea
          rows={3}
          placeholder="Remarks (optional)"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
      </Modal>
    </SmartRtpLayout>
  );
}
